import React, { useState, useEffect, useMemo } from "react";
import {
  Typography, Grid, Button, Box, TextField, FormControl,
  MenuItem, Select, Divider, Chip, InputAdornment, FormHelperText,
  Avatar, Paper, CircularProgress, Snackbar, Alert, Autocomplete, 
  Switch, FormControlLabel
} from "@mui/material";
import { useSearchParams } from 'react-router-dom';
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import TikTokIcon from '../../common/TikTokIcon';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from "prop-types";

import { addProfilePage, updateProfilePage, inviteFacilitator } from "../../../actions/profile";
import { areAllValuesEmpty, urlImg } from "../../common/helpers";
import { nombreDe, fotoDe, ESTADO_FACILITADOR } from "../../common/imparte";
import random from "../../common/random";
import typeImg from "../../common/typeImg";
import ModalDireccion from "../../modals/ModalDireccion";
import ModalBorradorPagina from "../../modals/ModalBorradorPagina";
import ServicioDePagina from "./ServicioDePagina";

import { useSelector, useDispatch } from "react-redux";

const FormPagina = ({
  irA,
}) => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const draftId = searchParams.get("draft");
  const typeParam = searchParams.get("type");
  const pageId = editId || draftId;

  const dispatch = useDispatch();
  const categories = useSelector(state => state.cat.categories);
  const direcciones = useSelector(state => state.profile.profile.direcciones);
  const paginas = useSelector(state => state.profile.profile.paginas);
  const currentUser = useSelector(state => state.user.user);
  const userServices = useSelector(state => state.profile.profile.servicios);

  const [modalDireccion, setModalDireccion] = useState(false);
  const [confirmarBorrador, setConfirmarBorrador] = useState(false);
  const [errorBorrador, setErrorBorrador] = useState(false);
  const [service, setService] = useState({
    nombre: "",
    descripcion: "",
    incluye: "",
    adicionalInfo: "",
    categoria: "",
    ubicacion: "",
    // Social Media
    instagram: "",
    youtube: "",
    facebook: "",
    tiktok: "",
    linkedin: "",
    x: "",
    tags: [],
    status: true,
  });

  const [tagInput, setTagInput] = useState("");

  // Quién imparte cada servicio: selectedServices guarda { servicio, facilitadores: [id] } (uno como mucho; vacío = lo imparte el
  // dueño) y `personas` los datos de esas personas (nombre y foto) para pintarlas.
  const [selectedServices, setSelectedServices] = useState([]);
  const [personas, setPersonas] = useState({});
  // Lo que ya está guardado en la página: { [servicio]: { persona, estado } } con el estado de su invitación
  // ('pendiente' | 'aceptado' | 'rechazado'). Sirve para saber si la persona elegida es nueva o ya respondió.
  const [guardado, setGuardado] = useState({});
  const [reenviando, setReenviando] = useState("");
  // Servicios de una página guardada que tenían varios facilitadores (id → cuántos): solo para avisar, ver el efecto de carga
  const [facilitadoresRecortados, setFacilitadoresRecortados] = useState({});
  const [banner, setBanner] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errores, setErrores] = useState({});
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

  const ownerId = currentUser?._id ? String(currentUser._id) : "";
  const paginaGuardada = useMemo(
    () => (pageId && paginas ? paginas.find(p => String(p._id) === String(pageId)) : null) || null,
    [pageId, paginas]
  );

  // Grupo/negocio o independiente: lo dice el enlace al crear o editar; si no, el tipo guardado de la página
  // (las páginas de antes no lo traían: eran grupo si tenían a alguien más en el equipo).
  const isGroup = useMemo(() => {
    if (typeParam === 'group') return true;
    if (typeParam === 'independent') return false;
    if (!paginaGuardada) return false;
    if (paginaGuardada.tipo) return paginaGuardada.tipo === 'grupo';
    return (paginaGuardada.miembros || []).length > 1;
  }, [typeParam, paginaGuardada]);

  useEffect(() => {
    if (pageId && paginas) {
      const pageToEdit = paginas.find(p => p._id.toString() === pageId.toString());
      if (pageToEdit) {
        // Datos (nombre y foto) de las personas del equipo
        const delEquipo = {};
        (pageToEdit.miembros || []).forEach(m => {
          const u = m.usuario || m;
          if (u && u._id) delEquipo[String(u._id)] = u;
        });
        setPersonas(delEquipo);
        // Cada servicio tiene UNA sola persona que lo imparte y debe estar en el equipo. Las páginas guardadas antes de esta
        // regla pueden traer a varias (o a alguien que ya no está): se conserva a la primera que sigue en el equipo y, si eran
        // varias, se avisa en el servicio. Si es el dueño, es lo mismo que no elegir a nadie: lo imparte él.
        // Nada cambia en la base hasta que la persona guarda la página.
        const duenoDeLaPagina = String(pageToEdit.propietario?._id || pageToEdit.propietario || "");
        const eranVarios = {};
        const yaGuardado = {};
        setSelectedServices((pageToEdit.servicios || []).map(s => {
          const serviceId = s.servicio?._id || s.servicio || s._id || s;
          const facilitators = s.facilitadores || (s.facilitador ? [s.facilitador] : []);
          const validos = [...new Set(facilitators.map(f => String(f?._id || f)))]
            .filter(f => f !== duenoDeLaPagina && Boolean(delEquipo[f]));
          if (validos.length > 1) eranVarios[serviceId] = validos.length;
          if (validos.length && ESTADO_FACILITADOR[s.estadoFacilitador]) {
            yaGuardado[serviceId] = { persona: validos[0], estado: s.estadoFacilitador };
          }
          return { servicio: serviceId, facilitadores: validos.slice(0, 1) };
        }));
        setFacilitadoresRecortados(eranVarios);
        setGuardado(yaGuardado);
        setService({
          nombre: pageToEdit.nombre || "",
          descripcion: pageToEdit.descripcion || "",
          incluye: pageToEdit.incluye || "",
          adicionalInfo: pageToEdit.infoAdicional || "", // careful with field name mapping
          categoria: pageToEdit.categoria || "",
          // Nombre resuelto por el backend: permite seguir mostrando la categoría actual aunque haya sido deshabilitada.
          categoriaNombre: pageToEdit.categoriaNombre || "",
          ubicacion: pageToEdit.ubicacion || "",
          instagram: pageToEdit.redes?.instagram || "",
          youtube: pageToEdit.redes?.youtube || "",
          facebook: pageToEdit.redes?.facebook || "",
          tiktok: pageToEdit.redes?.tiktok || "",
          linkedin: pageToEdit.redes?.linkedin || "",
          x: pageToEdit.redes?.x || "",
          tags: pageToEdit.tags || [],
          status: pageToEdit.status !== undefined ? pageToEdit.status : true,
        });

        if (pageToEdit.img) {
          setBannerPreview(`${urlImg}/paginas/${pageId}/${pageToEdit.img}`);
          // Note: actual file object 'banner' remains null unless user uploads new one
        }
      }
    }
  }, [pageId, paginas]);

  // Opciones de categoría: solo activas para elegir. Si se edita una página cuya categoría
  // fue deshabilitada, se agrega esa categoría actual para que siga mostrándose (sin poder
  // seleccionar otras deshabilitadas).
  const categoriaOptions = useMemo(() => {
    const activas = categories || [];
    if (service.categoria && !activas.some(c => c._id === service.categoria)) {
      return [...activas, { _id: service.categoria, name: service.categoriaNombre || 'Categoría actual (deshabilitada)' }];
    }
    return activas;
  }, [categories, service.categoria, service.categoriaNombre]);

  // Servicios que se pueden elegir: solo los de la categoría de la página (la categoría engloba sus subcategorías).
  const categoriaId = service.categoria ? String(service.categoria) : "";
  const categoriaNombre = categoriaOptions.find(c => c._id === service.categoria)?.name || service.categoriaNombre || "";
  const idsElegidos = useMemo(() => new Set(selectedServices.map(s => String(s.servicio))), [selectedServices]);
  const serviciosDeCategoria = useMemo(
    () => (userServices || []).filter(s => categoriaId && String(s.categoria) === categoriaId),
    [userServices, categoriaId]
  );
  // Los ya agregados que NO son de esa categoría: páginas guardadas antes de esta regla, o si se cambió la categoría.
  // Se muestran aparte y marcados (no se quitan solos) para que el dueño decida qué hacer.
  const serviciosFuera = useMemo(
    () => (categoriaId ? (userServices || []).filter(s => idsElegidos.has(String(s._id)) && String(s.categoria) !== categoriaId) : []),
    [userServices, idsElegidos, categoriaId]
  );
  const serviciosVisibles = [...serviciosFuera, ...serviciosDeCategoria];
  const idsFuera = new Set(serviciosFuera.map(s => String(s._id)));
  // Al crear un servicio desde aquí se le propone la categoría de la página (si sigue habilitada)
  const categoriaActiva = (categories || []).some(c => c._id === service.categoria);
  const rutaNuevoServicio = `/user/crear-servicio/0${categoriaActiva ? `?categoria=${service.categoria}` : ""}`;

  // La persona elegida para impartir un servicio (o null si lo imparte el dueño) y el estado de su invitación si ya está
  // guardada tal cual (null si la elección es nueva: se le invitará al guardar la página).
  const elegidoDe = (serviceId) => {
    const fId = selectedServices.find(s => s.servicio === serviceId)?.facilitadores?.[0];
    return fId ? (personas[String(fId)] || null) : null;
  };
  const estadoDe = (serviceId) => {
    const fId = selectedServices.find(s => s.servicio === serviceId)?.facilitadores?.[0];
    const previo = guardado[serviceId];
    return fId && previo && String(previo.persona) === String(fId) ? previo.estado : null;
  };

  // Elegir quién imparte un servicio: una persona (se guarda también para pintarla) o null = lo imparte el dueño
  const handleElegir = (serviceId, persona) => {
    // Al elegir quién se queda, el aviso de "tenía varios" deja de aplicar
    setFacilitadoresRecortados(prev => ({ ...prev, [serviceId]: 0 }));
    if (persona) setPersonas(prev => ({ ...prev, [String(persona._id)]: persona }));
    const facilitadores = persona ? [String(persona._id)] : [];
    setSelectedServices(prev => (
      prev.find(s => s.servicio === serviceId)
        ? prev.map(s => s.servicio === serviceId ? { ...s, facilitadores } : s)
        : [...prev, { servicio: serviceId, facilitadores }]
    ));
  };

  // Volver a avisar a quien aún no responde (o invitar de nuevo a quien rechazó). La invitación ya existe en el servidor: no
  // hace falta guardar la página, y lo que haya a medio editar en el formulario no se toca.
  const handleReenviar = async (serviceId) => {
    const persona = guardado[serviceId]?.persona;
    if (!pageId || !persona) return;
    setReenviando(serviceId);
    const res = await dispatch(inviteFacilitator(pageId, persona, serviceId));
    setReenviando("");
    if (res.success && res.estado === 'pendiente') {
      setGuardado(prev => ({ ...prev, [serviceId]: { persona, estado: 'pendiente' } }));
    }
    setAlert({ open: true, message: res.msg || (res.success ? "Invitación enviada" : "No se pudo enviar la invitación"), severity: res.success ? "success" : "error" });
  };

  // Las personas que imparten algún servicio de la página (el equipo, además del dueño) con el estado que se verá al guardar
  const equipo = useMemo(() => {
    const ids = [...new Set(selectedServices.map(s => s.facilitadores?.[0]).filter(Boolean).map(String))];
    return ids.map(id => personas[id]).filter(Boolean);
  }, [selectedServices, personas]);
  // Servicios cuya persona es nueva (o cambió): a ellas se les mandará la invitación al guardar
  const porInvitar = useMemo(
    () => selectedServices.filter(s => {
      const fId = s.facilitadores?.[0];
      return fId && !(guardado[s.servicio] && String(guardado[s.servicio].persona) === String(fId));
    }),
    [selectedServices, guardado]
  );

  const quitarFueraDeCategoria = () => {
    setSelectedServices(prev => prev.filter(s => !idsFuera.has(String(s.servicio))));
  }

  const handleToggleService = (id) => {
    setSelectedServices(prev => {
      if (prev.find(s => s.servicio === id)) {
        return prev.filter(s => s.servicio !== id);
      } else {
        return [...prev, { servicio: id, facilitadores: [] }];
      }
    });
  }

  // Crear un servicio saca del formulario: si hay algo escrito, antes se avisa que se guardará
  // como borrador (con ModalBorradorPagina); si está vacío no hay nada que guardar y se va directo.
  const handleCreateNewService = () => {
    if (!tieneContenido()) return irA(rutaNuevoServicio);
    setErrorBorrador(false);
    setConfirmarBorrador(true);
  }

  const guardarBorradorYContinuar = async () => {
    const guardado = await handleSubmit(null, true, false);
    if (guardado) {
      // El formulario de servicio es otra pestaña de la MISMA ruta: este formulario no se desmonta al ir allá, solo queda
      // oculto, y el modal (un portal) seguiría encima. Hay que cerrarlo aquí.
      setConfirmarBorrador(false);
      // `replace`: la página ya quedó guardada; con "Atrás" no se vuelve a este formulario viejo (guardarlo otra vez
      // crearía un segundo borrador), sino a donde se estaba antes.
      irA(rutaNuevoServicio, { replace: true });
    } else {
      setErrorBorrador(true);
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() !== "" && !service.tags.includes(tagInput.trim())) {
      setService(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  }

  const handleRemoveTag = (tagToDelete) => {
    setService(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  }

  const handleChange = (field, value) => {
    setService(prev => ({
      ...prev,
      [field]: value
    }));
    if (errores[field]) {
      setErrores(prev => ({ ...prev, [field]: null }));
    }
  }

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBanner(file);
      setBannerPreview(URL.createObjectURL(file));
      if (errores.banner) {
        setErrores(prev => ({ ...prev, banner: null }));
      }
    }
  }

  // ¿Hay algo escrito que valga la pena guardar como borrador?
  const tieneContenido = () => Object.values(service).some(val => {
    if (typeof val === 'string') return val.trim() !== "";
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object' && val !== null) return !areAllValuesEmpty(val);
    return false;
  }) || banner !== null || bannerPreview !== null;

  const handleSubmit = async (e, borrador = false, redirect = true) => {
    if (e) e.preventDefault();
    setLoading(true);

    if (borrador) {
      // Check if at least one field has data
      if (!tieneContenido()) {
        setAlert({
          open: true,
          message: "Se requiere al menos un campo para guardar el borrador",
          severity: "warning"
        });
        setLoading(false);
        return;
      }
    } else {
      // Validation for publish
      const requiredFields = ['nombre', 'descripcion', 'categoria'];
      const newErrors = {};

      requiredFields.forEach(field => {
        if (!service[field] || service[field].trim() === '') {
          newErrors[field] = "Este campo es requerido";
        }
      });

      if (!service.ubicacion || service.ubicacion.trim() === '') {
        newErrors.ubicacion = "Este campo es requerido";
      }

      const hasImage = banner !== null || bannerPreview !== null;
      if (!hasImage) {
        newErrors.banner = "La imagen es requerida";
      }

      if (serviciosFuera.length > 0) {
        newErrors.servicios = serviciosFuera.length === 1
          ? `Quita de la página el servicio que no es de la categoría «${categoriaNombre}» o cambia la categoría.`
          : `Quita de la página los servicios que no son de la categoría «${categoriaNombre}» o cambia la categoría.`;
      }

      if (Object.keys(newErrors).length > 0) {
        setErrores(newErrors);
        if (newErrors.servicios) setAlert({ open: true, message: newErrors.servicios, severity: "error" });
        setLoading(false);
        return false;
      }
    }

    const formData = {
      ...service,
      borrador,
      status: service.status,
      tags: service.tags,
      infoAdicional: service.adicionalInfo
    };

    // Quién imparte cada servicio: la persona elegida o, si no se eligió a nadie (y siempre en una página independiente), el
    // dueño. El equipo de la página son el dueño y las personas elegidas; el servidor les manda la invitación al guardar.
    formData.tipo = isGroup ? 'grupo' : 'independiente';
    formData.servicios = selectedServices.map(s => ({
      servicio: s.servicio,
      facilitadores: isGroup && s.facilitadores?.length ? [s.facilitadores[0]] : [ownerId]
    }));
    formData.miembros = [...new Set([ownerId, ...formData.servicios.map(s => s.facilitadores[0])])];

    // Prepare image data if banner exists
    let imgData = null;
    if (banner) {
      const identificador = random(7);
      const nombreImg = identificador + typeImg(banner);

      formData.img = nombreImg;

      imgData = new FormData();
      imgData.append('file', banner);
      imgData.append('nombre', identificador);
    }

    // { ok: true, invitaciones } si se guardó; si no, { ok: false, error } con el motivo que dio el servidor (o null)
    const resultado = pageId
      ? await dispatch(updateProfilePage(formData, pageId, imgData))
      : await dispatch(addProfilePage(formData, imgData));

    if (resultado?.ok) {
      if (redirect) {
        // Si salieron invitaciones se avisa en la lista de páginas, que es donde termina la persona
        const n = resultado.invitaciones || 0;
        const aviso = n > 0
          ? `Página guardada. Se ${n === 1 ? 'envió 1 invitación' : `enviaron ${n} invitaciones`}: ${n === 1 ? 'ese servicio se podrá' : 'esos servicios se podrán'} contratar cuando ${n === 1 ? 'la acepte' : 'las acepten'}.`
          : null;
        irA('/user/servicios', { state: { tab: 1, aviso } });
      } else {
        // Quien llamó decide a dónde ir y este formulario puede seguir montado: no dejar encendido el "Procesando..."
        setLoading(false);
      }
      return true;
    }

    setLoading(false);
    if (redirect) {
      setAlert({ open: true, message: resultado?.error || "No se pudo guardar la página. Inténtalo de nuevo.", severity: "error" });
    }
    return false;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          {pageId ? "Editar Página de Servicio" : "Crear Página de Servicio"}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={service.status}
              onChange={(e) => handleChange("status", e.target.checked)}
              color="primary"
              name="status"
            />
          }
          label={
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              {service.status ? "Página Activa" : "Página Inactiva"}
            </Typography>
          }
        />
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 3 }}
      >
        Completa la información de tu página de servicio para publicarlo
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600}>
              Información Básica
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Título de la Página<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <TextField
              id="service-name"
              placeholder="Ej: Escuela de Repostería Creativa"
              name="nombre"
              fullWidth
              value={service.nombre}
              onChange={(e) => handleChange("nombre", e.target.value)}
              variant="outlined"
              error={!!errores.nombre}
              helperText={errores.nombre}
            />
          </Grid>
          <Grid size={12}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Descripción<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
                </Typography>
              </Grid>
              <Grid>
                <Typography variant="caption" color="text.secondary">
                  {service.descripcion.length}/250
                </Typography>
              </Grid>
            </Grid>
            <TextField
              name="descripcion"
              placeholder="Describe tu página de servicio"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              value={service.descripcion}
              onChange={(e) => handleChange("descripcion", e.target.value.slice(0, 250))}
              error={!!errores.descripcion}
              helperText={errores.descripcion}
            />
          </Grid>

          <Grid size={12}>
            <Typography
              variant="body1"
              color="primary"
              fontWeight={600}
              sx={{ mt: 3, mb: 1 }}
            >
              Redes Sociales
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                { id: 'instagram', title: 'Instagram', placeholder: '@usuario', icon: <InstagramIcon fontSize="small" /> },
                { id: 'facebook', title: 'Facebook', placeholder: 'facebook.com/usuario', icon: <FacebookIcon fontSize="small" /> },
                { id: 'tiktok', title: 'TikTok', placeholder: '@usuario', icon: <TikTokIcon fontSize="small" /> },
                { id: 'youtube', title: 'YouTube', placeholder: 'youtube.com/@usuario', icon: <YouTubeIcon fontSize="small" /> },
                { id: 'x', title: 'X (Twitter)', placeholder: '@usuario', icon: <XIcon fontSize="small" /> },
                { id: 'linkedin', title: 'LinkedIn', placeholder: 'linkedin.com/in/usuario', icon: <LinkedInIcon fontSize="small" /> }
              ].map((social) => (
                <Grid size={{ xs: 12, md: 6 }} key={social.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', color: 'primary.main' }}>
                      {social.icon}
                    </Box>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      {social.title}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder={social.placeholder}
                    value={service[social.id]}
                    onChange={(e) => handleChange(social.id, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid size={12}>
            <Typography
              variant="body1"
              color="primary"
              fontWeight={600}
              sx={{ mt: 3, mb: 1 }}
            >
              Etiquetas / Palabras Clave
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2} sx={{ mb: 4 }} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Escribe una etiqueta"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="primary" sx={{ fontWeight: 'bold' }}>#</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleAddTag}
                  sx={{ height: '56px', fontWeight: 600, textTransform: 'none' }}
                >
                  Agregar
                </Button>
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {service.tags && service.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: '8px', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Banner de la Página
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Imagen de Portada<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box><br />
               (El banner debe de medir 1088 x 350 px para una mejor visualización)
            </Typography>
            {/* Vista previa a escala reducida manteniendo proporción real 1088x350 */}
            <Paper
              variant="outlined"
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                width: '100%',
                mx: 'auto',
                aspectRatio: '1088 / 350',
                borderRadius: 1,
                borderStyle: 'dashed',
                cursor: 'pointer',
                overflow: 'hidden',
                bgcolor: 'grey.50',
                borderColor: errores.banner ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                '&:hover .banner-overlay': { opacity: 1 },
              }}
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleBannerChange}
              />

              {bannerPreview ? (
                <>
                  {/* Imagen posicionada absolute para llenar todo el contenedor */}
                  <Box sx={{ position: 'absolute', inset: 0 }}>
                    <img
                      src={bannerPreview}
                      alt="Banner"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      crossOrigin="anonymous"
                    />
                  </Box>
                  {/* Overlay visible al hacer hover para indicar que se puede cambiar */}
                  <Box
                    className="banner-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(0,0,0,0.45)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <PhotoCameraIcon sx={{ color: 'white' }} />
                    <Typography sx={{ color: 'white', fontWeight: 500 }}>Cambiar banner</Typography>
                  </Box>
                </>
              ) : (
                // Placeholder centrado por el display flex del Paper
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoCameraIcon sx={{ color: errores.banner ? 'error.oscuro' : 'action.active' }} />
                  <Typography color={errores.banner ? 'error.oscuro' : 'primary'}>Agregar banner</Typography>
                </Box>
              )}
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              El banner medirá 1088 x 350 px
            </Typography>
            {errores.banner && <FormHelperText error>{errores.banner}</FormHelperText>}
          </Grid>

          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Dirección del Negocio
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Dirección fiscal o dirección del negocio<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <FormControl fullWidth error={!!errores.ubicacion}>
              <Select
                value={service.ubicacion}
                onChange={(e) => {
                  if (e.target.value === '__nueva__') {
                    setModalDireccion(true);
                  } else {
                    handleChange("ubicacion", e.target.value);
                  }
                }}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                }
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <Box component="em" sx={{ color: 'text.secondary', fontStyle: 'normal' }}>Selecciona tu dirección</Box>
                </MenuItem>
                {direcciones?.map(dir => (
                  <MenuItem key={dir._id} value={dir._id}>{dir.nombre}</MenuItem>
                ))}
                <MenuItem value="__nueva__">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="primary">Agregar dirección</Typography>
                  </Box>
                </MenuItem>
              </Select>
              {errores.ubicacion && <FormHelperText error>{errores.ubicacion}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Categorización
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Categoría<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <FormControl fullWidth error={!!errores.categoria}>
              <Autocomplete
                options={categoriaOptions}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                value={categoriaOptions.find(c => c._id === service.categoria) || null}
                onChange={(e, nueva) => handleChange("categoria", nueva ? nueva._id : "")}
                noOptionsText="Sin resultados"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Busca o selecciona una categoría"
                    error={!!errores.categoria}
                  />
                )}
              />
              {errores.categoria
                ? <FormHelperText error>{errores.categoria}</FormHelperText>
                : <FormHelperText sx={{ color: 'text.secondary' }}>Solo podrás agregar a la página servicios de esta categoría.</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ my: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight='bold' color='primary'>
                Servicios de la Página
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {categoriaId
                  ? `Elige entre tus servicios de la categoría «${categoriaNombre}» o crea uno nuevo`
                  : "Elige primero la categoría de tu página para ver tus servicios de esa categoría"}
              </Typography>
              {isGroup && (
                <Typography variant="body2" color="text.secondary">
                  En cada servicio que añadas decides quién lo imparte: tú u otra persona de tu equipo.
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              className="btn btn-secondary"
              onClick={handleCreateNewService}
            >
              Crear un nuevo servicio
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {serviciosFuera.length > 0 && (
            <Alert
              severity={errores.servicios ? "error" : "warning"}
              sx={{ mb: 3, borderRadius: "12px" }}
              action={
                <Button color="inherit" size="small" onClick={quitarFueraDeCategoria} sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}>
                  Quitar de la página
                </Button>
              }
            >
              {serviciosFuera.length === 1 ? "Este servicio no es" : `Estos ${serviciosFuera.length} servicios no son`} de la categoría «{categoriaNombre}»: {serviciosFuera.map(s => s.nombre).join(", ")}.
              {" "}{serviciosFuera.length === 1 ? "Quítalo" : "Quítalos"} para poder publicar o cambia la categoría de la página.
            </Alert>
          )}

          <Box sx={{ maxHeight: 600, overflowY: "auto", pr: 1 }}>
            {!categoriaId ? (
              <Paper sx={{ p: 4, textAlign: "center", bgcolor: "grey.50", borderRadius: "12px" }}>
                <Typography variant="body1" color="text.secondary">
                  Selecciona una categoría arriba y aquí aparecerán tus servicios de esa categoría.
                </Typography>
              </Paper>
            ) : serviciosVisibles.length > 0 ? (
              serviciosVisibles.map(sv => (
                <ServicioDePagina
                  key={sv._id}
                  service={sv}
                  fuera={idsFuera.has(String(sv._id))}
                  agregado={selectedServices.some(s => s.servicio === sv._id)}
                  esGrupo={isGroup}
                  duenoId={ownerId}
                  elegido={elegidoDe(sv._id)}
                  estado={estadoDe(sv._id)}
                  equipo={equipo}
                  recortados={facilitadoresRecortados[sv._id] || 0}
                  puedeReenviar={Boolean(pageId)}
                  reenviando={reenviando === sv._id}
                  onToggle={() => handleToggleService(sv._id)}
                  onElegir={(persona) => handleElegir(sv._id, persona)}
                  onReenviar={() => handleReenviar(sv._id)}
                  onVer={() => irA(`/user/servicio/${sv._id}`)}
                />
              ))
            ) : userServices && userServices.length > 0 ? (
              <Paper sx={{ p: 4, textAlign: "center", bgcolor: "grey.50", borderRadius: "12px" }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No tienes servicios en la categoría «{categoriaNombre}».
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Crea uno de esta categoría o cambia la categoría de la página.
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleCreateNewService}
                  sx={{ textTransform: "none", fontWeight: "bold", mt: 1 }}
                >
                  Crear un servicio de esta categoría
                </Button>
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: "center", bgcolor: "grey.50", borderRadius: "12px" }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No tienes servicios creados aún.
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleCreateNewService}
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Haz clic aquí para crear tu primer servicio
                </Button>
              </Paper>
            )}
          </Box>
        </Box>

        {isGroup && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight='bold' color='primary'>
              Equipo de la página
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Se arma solo con las personas que eliges para impartir tus servicios.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                avatar={<Avatar alt={currentUser?.name} src={fotoDe(currentUser, 'w_40,h_40,c_fill')} />}
                label="Tú"
                sx={{ bgcolor: 'sutil', borderRadius: '8px', fontWeight: 600 }}
              />
              {equipo.map(persona => (
                <Chip
                  key={persona._id}
                  avatar={<Avatar alt={persona.name} src={fotoDe(persona, 'w_40,h_40,c_fill')} />}
                  label={nombreDe(persona)}
                  sx={{ bgcolor: 'sutil', borderRadius: '8px' }}
                />
              ))}
            </Box>
            {porInvitar.length > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: "12px" }}>
                Al guardar {porInvitar.length === 1 ? 'se enviará 1 invitación' : `se enviarán ${porInvitar.length} invitaciones`}
                {' '}({[...new Set(porInvitar.map(s => nombreDe(personas[String(s.facilitadores[0])])).filter(Boolean))].join(', ')}).
                {' '}{porInvitar.length === 1 ? 'Ese servicio se podrá' : 'Esos servicios se podrán'} contratar cuando {porInvitar.length === 1 ? 'la acepte' : 'las acepten'}.
              </Alert>
            )}
          </Box>
        )}

        <Grid container spacing={2} mt={3} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={e => handleSubmit(e, true)}
            type="button"
            disabled={loading}
          >
            Guardar como borrador
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => irA("/user/servicios", { state: { tab: 1 } })}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Procesando..." : "Publicar página"}
          </Button>
        </Grid>

        <Snackbar
          open={alert.open}
          autoHideDuration={4000}
          onClose={() => setAlert({ ...alert, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: '30% !important' }} 
        >
          <Alert 
            onClose={() => setAlert({ ...alert, open: false })} 
            severity={alert.severity} 
            sx={{ 
              width: '100%', 
              minWidth: { xs: '300px', md: '500px' },
              fontSize: '1.2rem',
              py: 2,
              px: 4,
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              '& .MuiAlert-icon': { fontSize: '2rem' }
            }}
          >
            {alert.message}
          </Alert>
        </Snackbar>

      </form >

      {/* Agrega la dirección aquí mismo y la deja seleccionada, sin guardar borrador ni salir al perfil */}
      <ModalDireccion
        open={modalDireccion}
        onClose={() => setModalDireccion(false)}
        onSaved={(dir) => handleChange("ubicacion", dir._id)}
      />

      {/* Avisa que "Crear un nuevo servicio" guarda lo escrito como borrador antes de salir */}
      <ModalBorradorPagina
        open={confirmarBorrador}
        onClose={() => setConfirmarBorrador(false)}
        onConfirm={guardarBorradorYContinuar}
        guardando={loading}
        error={errorBorrador}
        publicada={!!pageId && (paginas || []).some(p => String(p._id) === String(pageId) && !p.borrador)}
      />
    </Box >
  )
}

FormPagina.propTypes = {
  irA: PropTypes.func.isRequired,
  type: PropTypes.string
}

export default FormPagina;
